import { atom } from "recoil";
import {
  startOfWeek,
  startOfMonth,
  endOfDay,
  startOfDay,
  subDays,
} from "date-fns";

export enum EHomeDateFilter {
  Today = "today",
  ThisWeek = "thisWeek",
  ThisMonth = "thisMonth",
  Last30Days = "last30Days",
}

// Format as a timezone-less local datetime ("YYYY-MM-DDTHH:mm:ss").
// We intentionally avoid toISOString()/"Z": the backend binds the value to a
// `timestamp without time zone` column, and a Kind=Utc DateTime makes Npgsql
// throw (HTTP 500). A kind-less local value also keeps the day boundaries in
// the user's timezone, which is what Today/This week/This month should mean.
const toLocalDateTimeString = (d: Date): string => {
  const pad = (n: number) => String(n).padStart(2, "0");
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` +
    `T${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`
  );
};

export const getHomeDateRange = (
  filter: EHomeDateFilter
): { fromDate: string; toDate: string } => {
  const now = new Date();
  let from: Date;
  switch (filter) {
    case EHomeDateFilter.Today:
      from = startOfDay(now);
      break;
    case EHomeDateFilter.ThisWeek:
      from = startOfWeek(now, { weekStartsOn: 0 });
      break;
    case EHomeDateFilter.Last30Days:
      from = startOfDay(subDays(now, 30));
      break;
    case EHomeDateFilter.ThisMonth:
    default:
      from = startOfMonth(now);
      break;
  }
  return {
    fromDate: toLocalDateTimeString(from),
    toDate: toLocalDateTimeString(endOfDay(now)),
  };
};

// Shared across the home page so both the admin stats view and the
// quote-creator widget react to the same date filter selection.
export const homeDateFilterState = atom<EHomeDateFilter>({
  key: "homeDateFilterState",
  default: EHomeDateFilter.ThisMonth,
});
